import type { ActivityDTO, InspectorDTO } from 'src/types/dto';
import dayjs from 'dayjs';
import { appointmentDisplayDurationDays } from 'src/utils/appointments';
import {
  formatNumberToMaxPlaces,
  isNonNegativeDecimalWithMaxPlaces,
} from 'src/utils/decimal-places';
import { durationHoursToDays, roundDuration } from 'src/utils/duration';

const MAX_DURATION_DECIMAL_PLACES = 5;

/**
 * Upper bound for a non-counting allocation. Matches backend
 * MAX_NON_COUNTING_ALLOCATION_DAYS. These days sit outside the activity audit-day budget.
 */
export const MAX_NON_COUNTING_ALLOCATION_DAYS = 365;

export type AllocationTabFiltersState = {
  preferredDay: dayjs.Dayjs | null;
  isOnlyInternal: boolean;
  isOnlyPreferred: boolean;
  isRecommended: boolean;
  selectedCompetencyIds: number[];
  selectedBusinessUnitIds: number[];
  searchText: string;
  durationDays: string;
  maxDistanceKm: string;
};

/** Merge parent-owned preferredDay with local filter fields so async emits cannot drop it. */
export function mergeAllocationTabFiltersState(
  parentFilters: Pick<AllocationTabFiltersState, 'preferredDay'>,
  localFilters: Omit<AllocationTabFiltersState, 'preferredDay'>,
  patch: Partial<AllocationTabFiltersState> = {}
): AllocationTabFiltersState {
  return {
    preferredDay: parentFilters.preferredDay,
    ...localFilters,
    ...patch,
  };
}

export function getActivityDurationDays(activity: ActivityDTO): number {
  if (activity?.durationDays != null && Number.isFinite(Number(activity.durationDays))) {
    return Number(activity.durationDays);
  }
  const hours = activity?.duration != null ? Number(activity.duration) : NaN;
  if (Number.isFinite(hours) && hours > 0) {
    return durationHoursToDays(hours);
  }
  return 0;
}

export function getDefaultAllocationDurationDays(activity: ActivityDTO): string {
  return formatNumberToMaxPlaces(getActivityDurationDays(activity), MAX_DURATION_DECIMAL_PLACES);
}

/** Person-days already allocated on saved appointments (counting roles only). */
export function getExistingAllocatedDurationDays(activity: ActivityDTO): number {
  return roundDuration(
    (activity?.appointments ?? [])
      .filter(
        (appointment) =>
          appointment.type !== 'blocker' &&
          appointment.projectRole?.countsTowardAudit !== false
      )
      .reduce((sum, appointment) => sum + appointmentDisplayDurationDays(appointment), 0)
  );
}

/**
 * When building a pending replacement team, save replaces existing appointments so the full
 * activity duration is available. Otherwise subtract what is already allocated on the activity.
 */
export function getAlreadyAllocatedDaysForAllocationBudget(
  activity: ActivityDTO,
  selected: SelectedInspectorEntry[]
): number {
  if (selected.length > 0) {
    return 0;
  }
  return getExistingAllocatedDurationDays(activity);
}

export function getDefaultRemainingAllocationDurationDays(activity: ActivityDTO): string {
  const remaining = getRemainingAllocationDurationDays(
    activity,
    [],
    getExistingAllocatedDurationDays(activity)
  );
  return formatNumberToMaxPlaces(remaining, MAX_DURATION_DECIMAL_PLACES);
}

/** Duration to assign when adding an auditor from the filter input. */
export function getDurationDaysForInspectorSelection(
  filterDurationDays: string,
  remainingDurationDays: number,
  countsTowardAudit: boolean
): number {
  if (!countsTowardAudit) {
    return 0;
  }
  const filterDuration = Number(filterDurationDays);
  if (Number.isFinite(filterDuration) && filterDuration > 0) {
    return Math.min(filterDuration, remainingDurationDays);
  }
  return Math.max(0, remainingDurationDays);
}

/** Filter values in effect when the inspector was added to the selection. */
export type InspectorSelectionFilterSnapshot = {
  preferredDay: string | null;
};

export type SelectedInspectorEntry = InspectorDTO & {
  selectedDurationDays: number;
  selectionFilters: InspectorSelectionFilterSnapshot;
  /** Whether this selection's project role counts toward audit duration. */
  countsTowardAudit: boolean;
  /**
   * Set once the scheduler types a value into this row's day field. Redistribution then
   * leaves the row alone and shares only what is left among the rows it still owns —
   * otherwise adding one more auditor would silently overwrite a number someone chose.
   */
  hasManualDuration?: boolean;
};

export function buildInspectorSelectionFilterSnapshot(
  preferredDay: { format: (template: string) => string; isValid: () => boolean } | null
): InspectorSelectionFilterSnapshot {
  return {
    preferredDay:
      preferredDay?.isValid() ? preferredDay.format('YYYY-MM-DD') : null,
  };
}

/** Only counting auditors contribute toward the activity duration budget. */
export function getTotalSelectedDurationDays(selected: SelectedInspectorEntry[]): number {
  return roundDuration(
    selected.reduce(
      (sum, inspector) =>
        inspector.countsTowardAudit ? sum + inspector.selectedDurationDays : sum,
      0
    )
  );
}

export function getRemainingAllocationDurationDays(
  activity: ActivityDTO,
  selected: SelectedInspectorEntry[],
  alreadyAllocatedDays = 0
): number {
  return roundDuration(
    Math.max(
      0,
      getActivityDurationDays(activity) -
        Math.max(0, alreadyAllocatedDays) -
        getTotalSelectedDurationDays(selected)
    )
  );
}

/** After deselecting inspector(s), restore duration when it was zeroed by a full allocation. */
export function getDurationDaysAfterInspectorDeselect(
  currentDurationDays: string,
  remainingAfterDeselect: number
): string {
  const current = Number(currentDurationDays);
  const remainingStr = formatNumberToMaxPlaces(remainingAfterDeselect, MAX_DURATION_DECIMAL_PLACES);
  if (Number.isFinite(current) && current > 0 && current <= remainingAfterDeselect) {
    return currentDurationDays;
  }
  return remainingStr;
}

/**
 * Keeps explicit per-auditor durations from the filter when they fit the budget;
 * otherwise equal-splits the remaining allocatable days.
 */
function shouldResetTeamEarliestAvailabilityPreview(
  prior: SelectedInspectorEntry | undefined,
  entry: SelectedInspectorEntry
): boolean {
  if (prior == null) {
    return true;
  }
  return (
    roundDuration(Number(prior.selectedDurationDays)) !==
    roundDuration(Number(entry.selectedDurationDays))
  );
}

/**
 * Suitable-inspector list previews use the filter duration, not the row's audit days after
 * split/redistribution. Drop inherited previews when duration changes or a row is new.
 */
export function syncTeamAvailabilityAfterSelectionChange(
  previous: SelectedInspectorEntry[],
  next: SelectedInspectorEntry[]
): SelectedInspectorEntry[] {
  const previousById = new Map(previous.map((entry) => [entry.id, entry]));
  return next.map((entry) => {
    if (shouldResetTeamEarliestAvailabilityPreview(previousById.get(entry.id), entry)) {
      return { ...entry, earliestAvailability: undefined };
    }
    return entry;
  });
}

/** Inspector IDs whose `teamEarliestAvailabilityByInspectorId` entry must be cleared on selection updates. */
export function inspectorIdsNeedingTeamEarliestAvailabilityReset(
  previous: SelectedInspectorEntry[],
  next: SelectedInspectorEntry[]
): number[] {
  const previousById = new Map(previous.map((entry) => [entry.id, entry]));
  const nextIds = new Set(next.map((entry) => entry.id));
  const ids = new Set<number>();
  next.forEach((entry) => {
    if (shouldResetTeamEarliestAvailabilityPreview(previousById.get(entry.id), entry)) {
      ids.add(entry.id);
    }
  });
  previous
    .filter((entry) => !nextIds.has(entry.id))
    .forEach((entry) => {
      ids.add(entry.id);
    });
  return Array.from(ids);
}

export function preserveOrRedistributeSelectedDurations(
  activity: ActivityDTO,
  selected: SelectedInspectorEntry[],
  alreadyAllocatedDays = 0
): SelectedInspectorEntry[] {
  const daysToSplit = getRemainingAllocationDurationDays(activity, [], alreadyAllocatedDays);
  const pendingTotal = getTotalSelectedDurationDays(selected);
  const hasUnallocatedCounting = selected.some(
    (inspector) =>
      inspector.countsTowardAudit && !(Number(inspector.selectedDurationDays) > 0)
  );

  if (pendingTotal > 0 && pendingTotal <= daysToSplit && !hasUnallocatedCounting) {
    return selected;
  }

  return redistributeSelectedInspectorDurations(activity, selected, daysToSplit);
}

/**
 * Redistributes activity duration equally among counting auditors.
 * Non-counting auditors keep the days the scheduler entered. Zero stays zero so save stays blocked.
 *
 * Rows the scheduler has typed a value into keep it, and only the days they don't claim
 * are shared among the remaining counting auditors. When every counting row is manual
 * there is nothing left to split and the selection passes through untouched.
 */
export function redistributeSelectedInspectorDurations(
  activity: ActivityDTO,
  selected: SelectedInspectorEntry[],
  allocatableDays?: number
): SelectedInspectorEntry[] {
  const activityDays = getActivityDurationDays(activity);
  const daysToSplit =
    allocatableDays != null && Number.isFinite(allocatableDays)
      ? Math.max(0, allocatableDays)
      : activityDays;

  const manualDays = roundDuration(
    selected.reduce(
      (sum, inspector) =>
        inspector.countsTowardAudit && inspector.hasManualDuration
          ? sum + inspector.selectedDurationDays
          : sum,
      0
    )
  );
  const daysForAuto = Math.max(0, roundDuration(daysToSplit - manualDays));
  const countingCount = selected.filter(
    (inspector) => inspector.countsTowardAudit && !inspector.hasManualDuration
  ).length;
  const equalShare =
    countingCount > 0 && daysForAuto > 0 ? roundDuration(daysForAuto / countingCount) : 0;

  let assignedCounting = 0;
  let countingIndex = 0;

  return selected.map((inspector) => {
    if (inspector.countsTowardAudit) {
      if (inspector.hasManualDuration) {
        return inspector;
      }
      const isLastCounting = countingIndex === countingCount - 1;
      countingIndex += 1;
      const share =
        daysForAuto <= 0
          ? 0
          : isLastCounting
            ? roundDuration(daysForAuto - assignedCounting)
            : equalShare;
      if (!isLastCounting && daysForAuto > 0) {
        assignedCounting = roundDuration(assignedCounting + share);
      }
      return { ...inspector, selectedDurationDays: share };
    }

    return inspector;
  });
}

/**
 * True when the pending counting rows must not be saved.
 *
 * `getRemainingAllocationDurationDays` clamps at zero, so an over-allocated total looks
 * identical to exact coverage and the coverage check alone cannot catch it. Mirrors the
 * backend's rules: every explicit allocation must be greater than zero, and their sum
 * must fit the activity duration.
 */
export function hasInvalidCountingDurations(
  activity: ActivityDTO,
  selected: SelectedInspectorEntry[]
): boolean {
  const counting = selected.filter((inspector) => inspector.countsTowardAudit);
  if (counting.some((inspector) => !(Number(inspector.selectedDurationDays) > 0))) {
    return true;
  }
  return getTotalSelectedDurationDays(selected) > getActivityDurationDays(activity);
}

/** Non-counting auditors must have explicit positive duration like any other role. */
export function hasInvalidNonCountingDurations(selected: SelectedInspectorEntry[]): boolean {
  const nonCounting = selected.filter((inspector) => inspector.countsTowardAudit === false);
  return nonCounting.some((inspector) => !(Number(inspector.selectedDurationDays) > 0));
}

/**
 * Whether a Team-table row's audit days can be typed into.
 *
 * Pending counting rows qualify **even at zero days**: redistribution hands a new auditor
 * 0 when a manual row already claims the whole duration, and that row has to stay typeable
 * or Save is blocked by a value nobody can reach. Already-allocated rows carry no
 * `selectedDurationDays` and stay read-only.
 */
export function isDurationRowEditable(row: {
  countsTowardAudit?: boolean;
  selectedDurationDays?: number | null;
}): boolean {
  if (typeof row.selectedDurationDays !== 'number' || !Number.isFinite(row.selectedDurationDays)) {
    return false;
  }
  if (row.countsTowardAudit === false) {
    return true;
  }
  return row.countsTowardAudit === true;
}

export interface DurationInputBlurResult {
  /** What the field should display. */
  text: string;
  /** Whether the field should show the over-budget error. */
  error: boolean;
  /** New value to commit, absent when nothing changed. */
  commit?: number;
}

/**
 * Decide what an audit-day input shows and commits when it loses focus.
 *
 * Snapping an out-of-range entry down to `maxDays` is right while there is budget to snap
 * to. At zero budget it would commit a zero-day allocation the backend rejects, so the row
 * reverts to its stored value and keeps the over-budget error until another counting row
 * frees days. Invalid or empty input reverts the same way rather than clearing an error
 * the row still has.
 */
export function resolveDurationInputBlur({
  text,
  value,
  maxDays,
  allowClear = false,
}: {
  text: string;
  value: number;
  maxDays: number;
  /** Non-counting rows can be cleared back to zero so save is blocked until days are entered. */
  allowClear?: boolean;
}): DurationInputBlurResult {
  if (allowClear && (text.trim() === '' || Number(text) === 0)) {
    return {
      text: '0',
      error: false,
      ...(roundDuration(value) !== 0 && { commit: 0 }),
    };
  }
  const overBudget = maxDays <= 0 || value > maxDays;
  const num = Number(text);
  const committable =
    maxDays > 0 &&
    isNonNegativeDecimalWithMaxPlaces(text, MAX_DURATION_DECIMAL_PLACES) &&
    Number.isFinite(num) &&
    num > 0;

  if (!committable) {
    return {
      text: formatNumberToMaxPlaces(value, MAX_DURATION_DECIMAL_PLACES),
      error: overBudget,
    };
  }

  const clamped = roundDuration(Math.min(num, maxDays));
  return {
    text: formatNumberToMaxPlaces(clamped, MAX_DURATION_DECIMAL_PLACES),
    error: false,
    ...(clamped !== roundDuration(value) && { commit: clamped }),
  };
}
